import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { DemandeEnAttente } from './useModificationsSensibles';
import { appelApi } from './api';

// ---------------------------------------------------------------------------
// Évaluations en cours de formation — ECF (CDC §7.5). Les règles — état,
// refus de planification, de saisie et de retrait, bilan — sont tenues par
// l'API : chaque ligne porte ce qui l'empêche, l'écran ne recalcule rien.
// ---------------------------------------------------------------------------

export type ResultatEcf = 'ACQUIS' | 'PARTIELLEMENT_ACQUIS' | 'NON_ACQUIS' | 'ABSENT';
export type EtatEvaluationEcf = 'PLANIFIEE' | 'A_SAISIR' | 'SAISIE';
export type EtatBlocEcf =
  | 'A_PLANIFIER'
  | 'PLANIFIEE'
  | 'A_SAISIR'
  | 'ACQUIS'
  | 'PARTIELLEMENT_ACQUIS'
  | 'NON_ACQUIS';

/** Choix proposés à la saisie, dans l'ordre de la grille. */
export const RESULTATS_ECF: { valeur: ResultatEcf; libelle: string }[] = [
  { valeur: 'ACQUIS', libelle: 'Acquis' },
  { valeur: 'PARTIELLEMENT_ACQUIS', libelle: 'Partiellement acquis' },
  { valeur: 'NON_ACQUIS', libelle: 'Non acquis' },
  { valeur: 'ABSENT', libelle: 'Absent' },
];

export interface CorrectionEcf {
  le: string;
  par: string | null;
  ancien: string;
  nouveau: string;
  ancienneObservation: string | null;
  nouvelleObservation: string | null;
  motif: string;
}

export interface LigneEcf {
  id: string;
  apprenantId: string;
  apprenant: string;
  siteCode: string;
  promotionCle: string;
  promotion: string;
  blocId: string;
  bloc: string;
  datePrevue: string;
  etat: EtatEvaluationEcf;
  etatLibelle: string;
  etatTon: string;
  resultat: ResultatEcf | null;
  resultatLibelle: string | null;
  resultatTon: string | null;
  observation: string | null;
  saisiLe: string | null;
  saisiPar: string | null;
  refusSaisie: string | null;
  refusRetrait: string | null;
  /** Ce qui empêche de demander la correction du résultat saisi (TF-437), ou `null`. */
  refusCorrection: string | null;
  demandeEnAttente: DemandeEnAttente | null;
  corrections: CorrectionEcf[];
}

export interface EtatBlocApprenant {
  etat: EtatBlocEcf;
  etatLibelle: string;
  etatTon: string;
  refus: string | null;
}

export interface PromotionEcf {
  cle: string;
  libelle: string;
  siteCode: string;
  formation: string;
  auProgramme: boolean;
  motif: string | null;
  blocs: { id: string; libelle: string; heures: number }[];
  joursEcf: string[];
  apprenants: { id: string; nom: string; blocs: Record<string, EtatBlocApprenant> }[];
}

export interface ReponseEcf {
  compteurs: {
    apprenants: number;
    attendues: number;
    realisees: number;
    acquises: number;
    aSaisir: number;
    planifiees: number;
  };
  promotions: PromotionEcf[];
  evaluations: LigneEcf[];
}

export interface BilanEcf {
  attendues: number;
  realisees: number;
  acquises: number;
  restantes: number;
  aSaisir: number;
}

export interface EvaluationDossierEcf {
  id: string;
  datePrevue: string;
  etat: EtatEvaluationEcf;
  etatLibelle: string;
  etatTon: string;
  resultat: ResultatEcf | null;
  resultatLibelle: string | null;
  resultatTon: string | null;
  observation: string | null;
  saisiLe: string | null;
  /** Équipe seulement. */
  saisiPar?: string | null;
  corrections?: CorrectionEcf[];
}

export interface BlocEcfDossier {
  id: string;
  libelle: string;
  heures: number | null;
  horsProgramme: boolean;
  etat: EtatBlocEcf;
  etatLibelle: string;
  etatTon: string;
  evaluations: EvaluationDossierEcf[];
}

/** ECF d'un dossier, bloc par bloc — carte du dossier et espace apprenant. */
export interface EcfDuDossier {
  auProgramme: boolean;
  motif: string | null;
  bilan: BilanEcf | null;
  blocs: BlocEcfDossier[];
}

/** « Tous les sites » se traduit par l'absence de filtre côté API. */
function parametresSite(site: string): string {
  return site && site !== 'TOUS' ? `?site=${encodeURIComponent(site)}` : '';
}

function invalider(client: QueryClient): Promise<unknown> {
  return Promise.all([
    client.invalidateQueries({ queryKey: ['ecf'] }),
    client.invalidateQueries({ queryKey: ['fin-formation'] }),
  ]);
}

export function useEcf(site: string) {
  const parametres = parametresSite(site);
  return useQuery({
    queryKey: ['ecf', parametres],
    queryFn: () => appelApi<ReponseEcf>(`/ecf${parametres}`),
  });
}

export function useEcfDossier(apprenantId: string) {
  return useQuery({
    queryKey: ['ecf', 'dossier', apprenantId],
    queryFn: () => appelApi<EcfDuDossier>(`/ecf/dossier/${encodeURIComponent(apprenantId)}`),
  });
}

export function usePlanifierEcf() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: { blocId: string; datePrevue: string; apprenantIds: string[] }) =>
      appelApi<{ planifiees: number; message: string }>('/ecf/planification', {
        methode: 'POST',
        corps,
      }),
    onSuccess: () => invalider(client),
  });
}

export interface SaisieResultatEcf {
  evaluationId: string;
  resultat: ResultatEcf;
  observation: string | null;
}

export function useSaisirResultatsEcf() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (resultats: SaisieResultatEcf[]) =>
      appelApi<{ saisies: number; message: string }>('/ecf/resultats', {
        methode: 'PUT',
        corps: { resultats },
      }),
    onSuccess: () => invalider(client),
  });
}

export function useRetirerEcf() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<void>(`/ecf/${encodeURIComponent(id)}`, { methode: 'DELETE' }),
    onSuccess: () => invalider(client),
  });
}
